import {
  BaseThemeType,
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  SearchOutlineIcon,
  styled,
  TextField,
} from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { FC, useEffect, useMemo, useState } from 'react'

export interface ResourceItem {
  id: number | string
  file_name: string
  url: string
}

export interface ResourceGroup {
  product_code: string
  product_title: string
  resources: ResourceItem[]
}

interface PropTypes {
  open: boolean
  onClose: () => void
  groups: ResourceGroup[]
  onResourceClick?: (url: string) => void
}

const OTHER_PRODUCTS_TITLE = 'Other Products'

const TitleRow = styled('div', { name: 'TitleRow' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: 16,
})

const SearchSection = styled('div', { name: 'SearchSection' })(({ theme }: { theme: BaseThemeType }) => ({
  padding: '16px 20px',
  borderBottom: `1px solid ${theme.palette.grey[130]}`,
}))

const SearchField = styled(TextField, { name: 'SearchField' })({
  width: '100%',
})

const ContentBody = styled('div', { name: 'ContentBody' })({
  padding: '8px 20px 24px',
})

const GroupWrapper = styled('div', { name: 'GroupWrapper' })({
  marginTop: 20,
  '&:first-of-type': {
    marginTop: 12,
  },
})

const GroupHeader = styled('div', { name: 'GroupHeader' })({
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '20px',
  marginBottom: 4,
})

const FileList = styled('div', { name: 'FileList' })({
  paddingLeft: 16,
})

const FileLink = styled(Link, { name: 'FileLink' })((props: { theme: BaseThemeType }) => ({
  display: 'block',
  padding: '4px 0',
  fontSize: 14,
  lineHeight: '20px',
  color: `${props.theme.palette.grey[900]} !important`,
  textDecoration: 'underline',
}))

const EmptyState = styled('p', { name: 'EmptyState' })((props: { theme: BaseThemeType }) => ({
  margin: '24px 0 8px',
  fontSize: 14,
  color: props.theme.palette.grey[700],
}))

const getGroupLabel = (group: ResourceGroup, otherProductsLabel: string) => {
  if (group.product_title === OTHER_PRODUCTS_TITLE) {
    return otherProductsLabel
  }
  return group.product_title || group.product_code
}

const ResourcesModal: FC<PropTypes> = ({ open, onClose, groups, onResourceClick }) => {
  const translate = useTranslate()
  const [query, setQuery] = useState('')
  const otherProductsLabel = translate(OTHER_PRODUCTS_TITLE)

  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const handleClose = () => {
    setQuery('')
    onClose()
  }

  const filteredGroups = useMemo(() => {
    const search = query.trim().toLowerCase()
    if (!search) return groups
    return groups
      .map((group) => {
        const productMatches =
          group.product_code.toLowerCase().includes(search) ||
          group.product_title.toLowerCase().includes(search) ||
          getGroupLabel(group, otherProductsLabel).toLowerCase().includes(search)
        if (productMatches) return group
        const matchingResources = group.resources.filter((resource) =>
          resource.file_name.toLowerCase().includes(search)
        )
        if (!matchingResources.length) return null
        return { ...group, resources: matchingResources }
      })
      .filter((group): group is ResourceGroup => group !== null)
  }, [groups, query, otherProductsLabel])

  const hasCatalog = groups.some((group) => group.resources.length > 0)
  const hasResults = filteredGroups.some((group) => group.resources.length > 0)
  const hasSearchQuery = query.trim().length > 0

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <TitleRow>
          <span>{translate('Resources & Datasheets')}</span>
          <DialogCloseButton onClick={handleClose} />
        </TitleRow>
      </DialogTitle>
      <DialogContent>
        <SearchSection>
          <SearchField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={translate('Search products or files')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlineIcon />
                </InputAdornment>
              ),
            }}
          />
        </SearchSection>
        <ContentBody>
          {hasSearchQuery && !hasResults ? (
            <EmptyState>{translate('No results found')}</EmptyState>
          ) : !hasCatalog ? null : (
            filteredGroups.map((group, index) => (
              <GroupWrapper key={`${group.product_code}-${group.product_title}-${index}`}>
                <GroupHeader>{getGroupLabel(group, otherProductsLabel)}</GroupHeader>
                <FileList>
                  {group.resources.map((resource) => (
                    <FileLink
                      key={resource.id}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={resource.file_name}
                      onClick={() => onResourceClick?.(resource.url)}
                    >
                      {resource.file_name}
                    </FileLink>
                  ))}
                </FileList>
              </GroupWrapper>
            ))
          )}
        </ContentBody>
      </DialogContent>
    </Dialog>
  )
}

export default ResourcesModal
